import { translateLabel } from "@/i18n/messages";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, MountainsIcon, RulerIcon } from "@phosphor-icons/react";
import { DIFFICULTY, STYLE_LABELS } from "@/lib/apiClient";
import { TrailStatusTrust } from "@/components/StatusTrust";
import { trailPath } from "@/lib/routes";

const readableType = (value) => String(value || "")
  .replaceAll("_", " ")
  .replace(/\b\w/g, (character) => character.toUpperCase());

export function TrailRow({
  trail,
  onSelect,
  onClick,
  onHover,
  selected = false,
  hovered = false,
  active = false,
}) {
  const d = DIFFICULTY[trail.difficulty] || DIFFICULTY.unknown;
  const elevation = trail.elevation_loss_m ?? trail.elevation_gain_m ?? trail.elevation_m;
  const trailType = trail.trail_type ? readableType(trail.trail_type) : null;
  const ridingStyle = trail.riding_style ? STYLE_LABELS[trail.riding_style] || readableType(trail.riding_style) : null;
  const showRidingStyle = ridingStyle && ridingStyle.toLowerCase() !== trailType?.toLowerCase();
  const highlighted = selected || hovered || active;
  const selectTrail = () => onSelect ? onSelect(trail.id) : onClick?.();

  const selectFromKeyboard = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    selectTrail();
  };

  const knownMeasurements = [
    trail.length_km != null
      ? <span key="length" className="flex items-center gap-1.5"><RulerIcon size={13} /> {trail.length_km} km</span>
      : null,
    elevation != null
      ? <span key="elevation" className="flex items-center gap-1.5"><MountainsIcon size={13} /> {Math.round(elevation)} m vertical</span>
      : null,
  ].filter(Boolean);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={selectTrail}
      onKeyDown={selectFromKeyboard}
      onMouseEnter={() => onHover?.(trail.id)}
      onMouseLeave={() => onHover?.(null)}
      data-testid="trail-row"
      data-trail-id={trail.id}
      data-selected={selected ? "true" : "false"}
      className={`group relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border bg-[#101012] px-4 py-3.5 pl-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/60 ${
        selected
          ? "border-[#CCFF00]/60 bg-[#CCFF00]/[0.06] shadow-[0_10px_35px_rgba(0,0,0,0.22)]"
          : highlighted
            ? "border-white/20 bg-[#151517]"
            : "border-white/[0.07] hover:-translate-y-px hover:border-white/20 hover:bg-[#151517]"
      }`}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-1 opacity-80 transition-opacity group-hover:opacity-100"
        style={{ backgroundColor: d.color }}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <span className="min-w-0 truncate font-display text-lg font-bold leading-tight text-white sm:text-xl">{trail.name}</span>
          <TrailStatusTrust trail={trail} compact />
        </div>

        {trail.description && (
          <p className="mt-1 truncate text-xs leading-relaxed text-zinc-500" title={trail.description}>
            {trail.description}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide"
            style={{ backgroundColor: `${d.color}18`, color: d.color }}
          >
            {translateLabel(d.label)}
          </span>
          {trailType && (
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold text-zinc-300">
              {trailType}
            </span>
          )}
          {showRidingStyle && (
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold text-zinc-300">
              {ridingStyle}
            </span>
          )}
          {selected && (
            <span className="rounded-full bg-[#CCFF00]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[#CCFF00]">
              Selected
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-zinc-400">
          {knownMeasurements.length > 0
            ? knownMeasurements
            : <span className="text-zinc-500">{translateLabel("More trail data coming soon")}</span>}
        </div>
      </div>

      <Link
        to={trailPath(trail)}
        onClick={(event) => event.stopPropagation()}
        data-testid="open-trail-detail"
        aria-label={`Open ${trail.name} details`}
        title={translateLabel("Open trail")}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-zinc-400 transition-all group-hover:border-[#CCFF00]/35 group-hover:bg-[#CCFF00]/10 group-hover:text-[#CCFF00] hover:translate-x-0.5"
      >
        <ArrowRightIcon size={19} />
      </Link>
    </div>
  );
}
